"use client";
"use no memo";

import { useQuery } from "@apollo/client/react";
import type { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { LuRefreshCcw } from "react-icons/lu";

import TrendingCoinsTable from "@/components/app/trade/trending-coins/trending-coins-table";
import TablePagination from "@/components/shared/table-pagination";
import UserMultipleSelect from "@/components/shared/users-search/user-multiple-select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FilterType, useFiltersAndSort } from "@/contexts/filters-sort-context";
import type { TradingRecentTrade } from "@/graphql/codegen/graphql";
import {
  TradingRecentTradesCashedDocument,
  TradingRecentTradesOrderBy,
} from "@/graphql/codegen/graphql";
import { DESO_APP_NAME } from "@/utils/constants";
import { getOrderByKey } from "@/utils/trade";
import type { PartialAccount } from "@/utils/types";

import type { Filters } from "../utils";
import { formatActivityTabFilters, formatFilters } from "../utils";
import type { ActivityTabSelection } from "./constants";
import {
  ActivityFilterFields,
  activityTabs,
  ActivityTabTypes,
  COLUMNS_WIDTH,
} from "./constants";
import { TradeActivityFilterPopover } from "./trade-activity-filter-popover";
import { columns as tableColumns } from "./trade-activity-table-columns";

export const INITIAL_FILTERS_AND_SORTS = {
  initialFilters: [],
  initialSort: null,
};

type SortingState = {
  id: TradingRecentTradesOrderBy;
  desc: boolean;
};

export const TradeActivityTableWrapper = () => {
  const [activeTab, setActiveTab] = useState<ActivityTabSelection>(
    activityTabs[0],
  );
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(25);
  const [columnSorting, setColumnSorting] = useState<SortingState[]>([
    {
      id: TradingRecentTradesOrderBy.TradeTimestampDesc,
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });
  const [refreshing, setRefreshing] = useState(false);
  const {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isFilterActive,
    updateFilter,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  } = useFiltersAndSort(INITIAL_FILTERS_AND_SORTS);

  const tokens =
    (findFilterByName(ActivityFilterFields.tokenPublicKey, true)
      ?.value as PartialAccount[]) ?? [];

  const setTokens = (tokens: PartialAccount[]) => {
    updateFilter(
      {
        name: ActivityFilterFields.tokenPublicKey,
        type: FilterType.multiUserSelect,
        value: tokens,
      },
      false,
      true,
    );
  };

  const methods = {
    findFilterByName,
    updateFilter,
    removeFilter,
    isFilterActive,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  };

  const activityQueryVariables = useMemo(() => {
    const formatFiltersParams =
      Object.keys(filters).length > 0 ? formatFilters(filters) : {};

    const variables: {
      first: number;
      offset: number;
      orderBy: TradingRecentTradesOrderBy;
      filter?: Filters;
    } = {
      first: itemsPerPage,
      offset: pageOffset,
      orderBy: getOrderByKey(
        columnSorting,
        TradingRecentTradesOrderBy,
        TradingRecentTradesOrderBy.TradeTimestampDesc,
      ),
      filter: {
        isMatchedOrder: {
          equalTo: false,
        },
      },
    };

    if (Object.keys(formatFiltersParams).length > 0) {
      variables.filter = {
        ...variables.filter,
        ...formatFiltersParams,
      };
    }

    return variables;
  }, [columnSorting, filters, itemsPerPage, pageOffset]);

  const {
    data: activityData,
    loading: loadingActivityCoins,
    refetch: refetchActivityData,
  } = useQuery(TradingRecentTradesCashedDocument, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: activityQueryVariables,
  });

  const columns = useMemo(() => tableColumns(), []);

  const activityCoins =
    (activityData?.tradingRecentTrades?.nodes?.filter(
      Boolean,
    ) as TradingRecentTrade[]) ?? [];
  const isFirstLoad = loadingActivityCoins && !activityData;
  const queryTotalCount = activityData?.tradingRecentTrades?.totalCount ?? 0;

  const refetchWithUpdatedParams = async () => {
    await refetchActivityData();
  };

  const onPaginate = async (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });
    setPageOffset(offset);
  };

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: itemsPerPage,
    });
    setPageOffset(0);
  };

  const handleColumnSortingChange = (sorting: SortingState[]) => {
    setColumnSorting(sorting);
    resetPagination();
  };

  const handleActiveTab = (tab: ActivityTabSelection) => {
    setActiveTab(tab);

    if (tab.type === ActivityTabTypes.All) {
      removeFilter("tradeValueUsd");
    } else {
      updateFilter({
        name: "tradeValueUsd",
        type: FilterType.numeric,
        value: formatActivityTabFilters(tab),
      });
    }

    resetPagination();
  };

  const handleResetFilters = () => {
    setActiveTab(activityTabs[0]);
    setFilters(INITIAL_FILTERS_AND_SORTS.initialFilters, true);
    setTokens([]);
    resetPagination();
  };

  const handleApplyFilters = () => {
    resetPagination();
  };

  const handleSelectedTokens = (tokens: PartialAccount[]) => {
    if (!tokens.length) {
      removeFilter(ActivityFilterFields.tokenPublicKey, true);
    } else {
      updateFilter(
        {
          name: ActivityFilterFields.tokenPublicKey,
          type: FilterType.multiUserSelect,
          value: tokens,
        },
        false,
        true,
      );
    }
    resetPagination();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetchActivityData();
    setRefreshing(false);
  };

  return isFirstLoad ? (
    <div className="flex flex-col">
      <div
        className="
          flex w-full flex-col items-start justify-normal gap-2
          xl:flex-row xl:items-end xl:justify-between
        "
      >
        <Skeleton
          className="
            mb-4 h-[38px] w-full
            xl:mb-0 xl:w-[250px]
          "
        />
        <Skeleton
          className="
            h-[38px] w-full
            xl:w-[535px]
          "
        />
      </div>
      <Skeleton className="mt-4 h-[620px] w-full" />
    </div>
  ) : (
    <div className="min-h-[700px]">
      <div
        className="
          mb-4 flex flex-col items-start justify-between gap-2
          xl:flex-row xl:items-center
        "
      >
        <div>
          <h2 className="font-sans text-xl font-semibold text-muted-foreground">
            Recent Trades
          </h2>
          <p className="text-muted">
            View all recent trades on the {DESO_APP_NAME} DEX
          </p>
        </div>

        <div
          className="
            flex w-full flex-col justify-between gap-6
            sm:flex-row sm:items-center sm:gap-2
            md:w-auto md:justify-normal
          "
        >
          <div
            className="
              w-full overflow-x-auto
              md:w-auto
            "
          >
            <div className="flex min-w-[315px] gap-x-1.5">
              {activityTabs.map((tab) => (
                <Button
                  key={tab.type}
                  onClick={() => handleActiveTab(tab)}
                  variant={
                    activeTab.type === tab.type ? "outlinePrimary" : "outline"
                  }
                  disabled={loadingActivityCoins}
                  size="lg"
                  className="px-4 py-2"
                >
                  {tab.content}
                </Button>
              ))}
            </div>
          </div>

          <UserMultipleSelect
            buttonClassName="w-[220px]"
            users={tokens}
            onSelectedUsers={handleSelectedTokens}
            placeholder="Search any token..."
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <TradeActivityFilterPopover
          methods={methods}
          filters={filters}
          onResetFilters={handleResetFilters}
          onApplyFilters={handleApplyFilters}
        />
        <Button
          variant="link"
          className="
            flex items-center gap-2 py-0 text-muted underline-offset-2
            hover:underline
          "
          size="xs"
          disabled={refreshing}
          onClick={handleRefresh}
        >
          <LuRefreshCcw />
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      <TrendingCoinsTable<TradingRecentTrade>
        loading={loadingActivityCoins}
        columns={columns}
        data={activityCoins}
        columnSorting={columnSorting}
        setColumnSorting={(sorting) =>
          handleColumnSortingChange(sorting as SortingState[])
        }
        wrapperClassName={
          !activityCoins.length ? "min-w-full" : "min-w-[1024px]"
        }
        pagination={pagination}
        pageOffset={pageOffset}
        columnsWidth={COLUMNS_WIDTH}
        refetchData={refetchWithUpdatedParams}
      />

      <TablePagination
        loading={loadingActivityCoins}
        pagination={pagination}
        onPaginate={onPaginate}
        itemsPerPage={itemsPerPage}
        totalCount={queryTotalCount}
      />
    </div>
  );
};
